import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Check, Clock, Utensils, ShoppingBag, MapPin, Phone } from "lucide-react";

const STEPS = [
  { id: "new", label: "En attente", desc: "Commande reçue", icon: Clock },
  { id: "preparing", label: "Confirmée", desc: "En préparation", icon: Utensils },
  { id: "ready", label: "Prête", desc: "À retirer", icon: ShoppingBag },
];

const STATUS_INDEX = { new: 0, preparing: 1, ready: 2, delivered: 2, completed: 2 };

export default function Tracking() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get(`/orders/${id}`);
      setOrder(data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => {
    load();
    const i = setInterval(load, 5000);
    return () => clearInterval(i);
  }, [id]);

  if (loading) return <div className="pt-32 text-center text-muted-foreground">Chargement…</div>;
  if (!order) return <div className="pt-32 text-center" data-testid="tracking-not-found">Commande introuvable.</div>;

  const currentStep = STATUS_INDEX[order.status] ?? 0;

  return (
    <div data-testid="tracking-page" className="pt-28 pb-20 min-h-[80vh] bg-background">
      <div className="max-w-2xl mx-auto px-6">
        <div className="text-center mb-12">
          <p className="uppercase tracking-[0.3em] text-xs text-primary mb-3">Suivi de commande</p>
          <h1 className="font-display text-5xl text-foreground mb-2">Commande #{order.order_number}</h1>
          <p className="text-muted-foreground">Bonjour {order.customer.firstname}, voici l'état de votre commande.</p>
        </div>

        {/* Steps */}
        <div data-testid="tracking-steps" className="bg-white border border-border rounded-sm p-8 mb-6">
          <div className="flex justify-between items-start relative">
            <div className="absolute top-6 left-12 right-12 h-px bg-border" />
            <div
              className="absolute top-6 left-12 h-px bg-primary transition-all duration-700"
              style={{ width: `calc(${(currentStep / (STEPS.length - 1)) * 100}% - 0px - ${currentStep === 0 ? 0 : currentStep === STEPS.length - 1 ? "3rem" : "1.5rem"})` }}
            />
            {STEPS.map((step, i) => {
              const Icon = step.icon;
              const done = i < currentStep;
              const active = i === currentStep;
              return (
                <div key={step.id} data-testid={`step-${step.id}`} className="relative flex flex-col items-center text-center flex-1 z-10">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center border-2 transition-all duration-500 ${
                    done || active ? "bg-primary border-primary text-white" : "bg-white border-border text-muted-foreground"
                  } ${active ? "scale-110 ring-4 ring-accent" : ""}`}>
                    {done ? <Check size={20} /> : <Icon size={20} />}
                  </div>
                  <div className="mt-3">
                    <div className={`font-medium ${active || done ? "text-foreground" : "text-muted-foreground"}`}>{step.label}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{step.desc}</div>
                  </div>
                </div>
              );
            })}
          </div>
          {order.status === "ready" && (
            <div className="mt-8 p-4 bg-accent rounded-sm text-center">
              <p className="font-display text-2xl text-primary mb-1">Votre commande est prête !</p>
              <p className="text-sm text-foreground">Venez la chercher à Rue de la Gare 8, 1110 Morges.</p>
            </div>
          )}
        </div>

        {/* Order summary */}
        <div className="bg-white border border-border rounded-sm p-6 mb-6">
          <h2 className="font-display text-2xl mb-4">Votre commande</h2>
          <div className="space-y-2">
            {order.items.map((it, i) => (
              <div key={i} className="flex justify-between text-sm">
                <span>{it.quantity}× {it.name}{it.addons?.length ? ` + ${it.addons.map(a => a.name).join(", ")}` : ""}</span>
                <span className="font-medium">{it.line_total.toFixed(2)} CHF</span>
              </div>
            ))}
          </div>
          <div className="border-t border-border pt-3 mt-3 flex justify-between text-lg font-semibold">
            <span>Total</span><span className="text-primary">{order.total.toFixed(2)} CHF</span>
          </div>
        </div>

        <div className="bg-primary text-white rounded-sm p-6 mb-6">
          <h3 className="font-display text-2xl mb-3">Retrait</h3>
          <div className="space-y-2 text-sm text-white/90">
            <div className="flex gap-3"><MapPin size={16} className="shrink-0 mt-0.5" />Rue de la Gare 8, 1110 Morges</div>
            <div className="flex gap-3"><Phone size={16} className="shrink-0 mt-0.5" /><a href="tel:0218020593" className="hover:underline">021 802 05 93</a></div>
          </div>
        </div>

        <p className="text-center text-xs text-muted-foreground">Cette page se met à jour automatiquement toutes les 5 secondes.</p>
        <div className="text-center mt-6">
          <Link to="/" className="text-primary hover:underline">← Retour à l'accueil</Link>
        </div>
      </div>
    </div>
  );
}
